import * as React from 'react'

import { CHUNK_GAP_MS, PLAYBACK_RATES, PLAYER_STATUS, PlayerStatus, SECTION_GAP_MS } from '../../components/readAloud/constants'
import { useReadAloudSource } from '../../components/readAloud/readAloudSource'
import { ClipSource, staticStringSourceFor } from '../../libs/speech/clipSource'
import { cancelSpeech, isSpeaking, pauseSpeech, prepareSpeech, resumeSpeech, setSpeechRate, speak, warmSpeech } from '../../libs/speech/speechManager'
import { buildWordTimeline, collectHtmlWords, collectNodeWords, wordIndexForCharIndex } from '../../libs/speech/speechText'
import { speechTextFromWords } from '../../libs/speech/wordCollector'

const START_POSITION = { chunkIndex: 0, wordIndex: 0, }

export interface PlayerChunk {
  content?: React.ReactNode;
  heading?: boolean;
  html?: string;
  source?: ClipSource | null;
  startsSection?: boolean;
}

interface PlayerPosition {
  chunkIndex: number;
  wordIndex: number;
}

interface ReadAloudPlayerOptions {
  chunks: PlayerChunk[];
  keepPlace?: boolean;
  onFinish?: () => void;
}

const useReadAloudPlayer = ({ chunks, keepPlace = true, onFinish, }: ReadAloudPlayerOptions) => {
  const [status, setStatus] = React.useState<PlayerStatus>(PLAYER_STATUS.IDLE)
  const [rate, setRate] = React.useState(PLAYBACK_RATES[0])
  const [activeChunkIndex, setActiveChunkIndex] = React.useState<number | null>(null)
  const [activeWordIndex, setActiveWordIndex] = React.useState<number | null>(null)
  const [prepared, setPrepared] = React.useState(false)
  const [pendingPlay, setPendingPlay] = React.useState<PlayerPosition | null>(null)

  const positionRef = React.useRef<PlayerPosition>(START_POSITION)
  const statusRef = React.useRef(status)
  statusRef.current = status
  const onFinishRef = React.useRef(onFinish)
  onFinishRef.current = onFinish
  const rateRef = React.useRef(rate)
  const generationRef = React.useRef(0)
  const enginePausedRef = React.useRef(false)
  const gapTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const inheritedSource = useReadAloudSource()
  const chunkData = React.useMemo(() => chunks.map((chunk) => {
    const words = chunk.html != null ? collectHtmlWords(chunk.html) : collectNodeWords(chunk.content)
    const speechText = speechTextFromWords(words)
    return { heading: !!chunk.heading, source: chunk.source ?? staticStringSourceFor(speechText) ?? inheritedSource, speechText, startsSection: !!chunk.startsSection, timeline: buildWordTimeline(speechText), wordCount: words.length, }
  }), [chunks, inheritedSource])

  React.useEffect(() => {
    let current = true
    setPrepared(false)
    const requests = chunkData.map(chunk => ({ source: chunk.source, text: chunk.speechText, }))
    const first = requests.find(request => request.text)
    const firstReady = first ? prepareSpeech([first]) : Promise.resolve()
    firstReady.then(() => {
      if (!current) { return }

      if (first) { warmSpeech(first.text) }
      setPrepared(true)
    })
    prepareSpeech(requests)
    return () => { current = false }
  }, [chunkData])

  const nextSpeakableIndex = (chunkIndex: number) => {
    const offset = chunkData.slice(chunkIndex + 1).findIndex(candidate => candidate.wordCount > 0)
    return offset === -1 ? null : chunkIndex + 1 + offset
  }

  const clearHighlight = () => {
    setActiveChunkIndex(null)
    setActiveWordIndex(null)
  }

  const finish = () => {
    positionRef.current = START_POSITION
    clearHighlight()
    setStatus(PLAYER_STATUS.IDLE)
  }

  const clearGap = () => {
    if (gapTimerRef.current === null) { return }

    clearTimeout(gapTimerRef.current)
    gapTimerRef.current = null
  }

  const invalidate = () => {
    generationRef.current += 1
    enginePausedRef.current = false
  }

  const interrupt = () => {
    invalidate()
    clearGap()
    cancelSpeech()
  }

  const interruptUnlessIdle = () => {
    if (statusRef.current !== PLAYER_STATUS.IDLE) { interrupt() }
  }

  const settleInterrupted = () => {
    invalidate()
    settle()
  }

  const speakFrom = (chunkIndex: number, wordIndex: number) => {
    const chunk = chunkData[chunkIndex]
    if (!chunk) {
      finish()
      return
    }
    if (!chunk.wordCount) {
      advanceFrom(chunkIndex)
      return
    }

    const startCharIndex = chunk.timeline[wordIndex] ? chunk.timeline[wordIndex].start : 0
    const generation = generationRef.current
    positionRef.current = { chunkIndex, wordIndex, }

    const handle = speak(chunk.speechText, {
      onBoundary: (charIndex) => {
        if (generationRef.current !== generation) { return }

        const index = wordIndexForCharIndex(chunk.timeline, charIndex)
        if (index === null) { return }

        positionRef.current = { chunkIndex, wordIndex: index, }
        setActiveWordIndex(index)
      },
      onEnd: () => {
        if (generationRef.current !== generation) { return }

        advanceFrom(chunkIndex)
      },
      onInterrupted: () => {
        if (generationRef.current !== generation) { return }

        settleInterrupted()
      },
      rate: rateRef.current,
      startCharIndex,
    })

    if (!handle) {
      finish()
      return
    }

    setActiveChunkIndex(chunkIndex)
    setActiveWordIndex(wordIndex)
    setStatus(PLAYER_STATUS.PLAYING)
    const nextIndex = nextSpeakableIndex(chunkIndex)
    if (nextIndex !== null) { warmSpeech(chunkData[nextIndex].speechText) }
  }

  const beatBefore = (nextIndex: number) => {
    positionRef.current = { chunkIndex: nextIndex, wordIndex: 0, }
    setActiveChunkIndex(nextIndex)
    setActiveWordIndex(null)
    gapTimerRef.current = setTimeout(() => {
      gapTimerRef.current = null
      if (isSpeaking()) {
        settleInterrupted()
        return
      }
      speakFrom(nextIndex, 0)
    }, chunkData[nextIndex].heading ? SECTION_GAP_MS : CHUNK_GAP_MS)
  }

  const advanceFrom = (chunkIndex: number) => {
    const nextIndex = nextSpeakableIndex(chunkIndex)
    if (nextIndex === null || chunkData[nextIndex].startsSection) {
      finish()
      if (onFinishRef.current) { onFinishRef.current() }
      return
    }
    beatBefore(nextIndex)
  }

  const playFrom = (chunkIndex: number, wordIndex = 0) => {
    interrupt()
    if (!prepared) {
      setPendingPlay({ chunkIndex, wordIndex, })
      return
    }
    speakFrom(chunkIndex, wordIndex)
  }

  const replay = () => {
    const { chunkIndex, wordIndex, } = positionRef.current
    playFrom(chunkIndex, wordIndex)
  }

  const playNow = () => {
    if (status === PLAYER_STATUS.PAUSED && enginePausedRef.current && resumeSpeech()) {
      enginePausedRef.current = false
      setStatus(PLAYER_STATUS.PLAYING)
      return
    }

    replay()
  }

  const play = playNow

  React.useEffect(() => {
    if (!prepared || !pendingPlay) { return }

    setPendingPlay(null)
    playFrom(pendingPlay.chunkIndex, pendingPlay.wordIndex)
  }, [prepared, pendingPlay])

  const pause = () => {
    if (pendingPlay) {
      setPendingPlay(null)
      return
    }
    if (status !== PLAYER_STATUS.PLAYING) { return }

    if (pauseSpeech()) {
      enginePausedRef.current = true
    } else {
      interrupt()
    }
    setStatus(PLAYER_STATUS.PAUSED)
  }

  const rewind = () => playFrom(0, 0)

  const cycleRate = () => {
    const nextRate = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(rate) + 1) % PLAYBACK_RATES.length]
    rateRef.current = nextRate
    setRate(nextRate)
    if (status === PLAYER_STATUS.IDLE || gapTimerRef.current !== null || setSpeechRate(nextRate)) { return }

    if (status === PLAYER_STATUS.PLAYING) {
      replay()
      return
    }

    interrupt()
  }

  const settle = () => {
    if (!keepPlace) {
      finish()
      return
    }

    clearHighlight()
    setStatus(PLAYER_STATUS.IDLE)
  }

  const stop = () => {
    setPendingPlay(null)
    if (status === PLAYER_STATUS.IDLE) { return }

    interrupt()
    settle()
  }

  const chunkDataRef = React.useRef(chunkData)
  React.useEffect(() => {
    if (chunkDataRef.current === chunkData) { return }

    chunkDataRef.current = chunkData
    interruptUnlessIdle()
    finish()
  }, [chunkData])

  React.useEffect(() => interruptUnlessIdle, [])

  return { activeChunkIndex, activeWordIndex, cycleRate, pause, pendingChunkIndex: pendingPlay?.chunkIndex ?? null, play, playFrom, playRequested: pendingPlay !== null, prepared, rate, rewind, status, stop, }
}

export default useReadAloudPlayer
